import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider } from './contexts/AuthContext';
import { CustomerFilterProvider } from './contexts/CustomerFilterContext';
import { initializeApi } from './services/api';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import ProtectedRouteStable from './components/Auth/ProtectedRouteStable';
import Login from './components/Auth/Login';
import ForgotPassword from './components/Auth/ForgotPassword';
import ResetPassword from './components/Auth/ResetPassword';
import CreatePassword from './components/Auth/CreatePassword';
import VerifySignupPhone from './components/Auth/VerifySignupPhone';
import Signup from './components/Signup/Signup';
import Layout from './components/Layout/Layout';
import Voicemail from './components/Voicemail/Voicemail';
import ServicePackages from './components/Admin/ServicePackages';
import Softphones from './components/Admin/Softphones';
import Extensions from './components/Admin/Extensions';
import ExtensionsQuota from './components/Admin/ExtensionsQuota';
import PhoneNumbers from './components/Admin/PhoneNumbers';
import ActivityLog from './components/Admin/ActivityLog';
import Profile from './components/Profile/Profile';
import Users from './components/Admin/Users';
import UsersAsExtensions from './components/Admin/UsersAsExtensions';
import DIDDeletion from './components/Admin/DIDDeletion';
import RingGroups from './components/Admin/RingGroups';
import RingGroupsV2 from './components/Admin/RingGroupsV2';
import Prompts from './components/Admin/Prompts';
import IVR from './components/Admin/IVR';
import TimeConditions from './components/Admin/TimeConditions';
import Calendar from './components/User/Calendar';
import UserCallHistory from './components/User/UserCallHistory';
import TeamPresence from './components/User/TeamPresence';
import ResellerManagement from './components/Admin/ResellerManagement';
import Customers from './components/Admin/Customers';
import CustomersAsExtensions from './components/Admin/CustomersAsExtensions';
import CustomerView from './components/Admin/CustomerView';
import Billing from './components/Admin/Billing';
import PaymentPosting from './components/Admin/PaymentPosting';
import PADFileManagement from './components/Admin/PADFileManagement';
// import DunningManagement from './components/Admin/DunningManagement';
// import DunningManagementSimple from './components/Admin/DunningManagementSimple';
import DunningNew from './components/Admin/DunningNew';
import BrandManagement from './components/Admin/BrandManagement';
import AddOnServices from './components/Admin/AddOnServices';
import InternetServices from './components/Admin/InternetServices';
import InternetVendorManagementV2 from './components/Admin/InternetVendorManagementV2';
import InternetServicesDebug from './components/Admin/InternetServicesDebug';
import AppErrorBoundary from './components/Common/AppErrorBoundary';
import BundleManagement from './components/Admin/BundleManagement';
import ErrorNotification from './components/Common/ErrorNotification';
import errorLogger from './services/errorLogger.service';
import ScheduledTasks from './components/Admin/ScheduledTasks';
import SignupManager from './components/Admin/SignupManager';
import CallHistory from './components/Admin/CallHistory';
import RecordingRules from './components/Admin/RecordingRules';
import RecordingSettings from './components/Admin/RecordingSettings';
import ErrorLogs from './components/Admin/ErrorLogs';
import PCICompliance from './components/Admin/PCICompliance';
import IssuesTracking from './components/Admin/IssuesTracking';
import ComingSoon from './components/Common/ComingSoon';
import DebugSettings from './components/Admin/DebugSettings';
import Porting from './components/Admin/Porting';
import config from './config';
import brandingService from './services/branding.service';
import AuthErrorBoundary from './components/Common/AuthErrorBoundary';
import { WebPhoneProvider } from './contexts/WebPhoneContext';
import { WebPhonePopup } from './components/WebPhone';

function App() {
  
  const [theme, setTheme] = useState(() => 
    createTheme({
      palette: {
        mode: 'light',
        primary: {
          main: '#1976d2',
        },
        secondary: {
          main: '#dc004e',
        },
        background: {
          default: '#f5f5f5',
        },
      },
      typography: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      },
      components: {
        MuiButton: {
          styleOverrides: {
            root: {
              textTransform: 'none',
            },
          },
        },
      },
    })
  );

  useEffect(() => {
    // Initialize API with runtime configuration
    initializeApi().catch(err => {
      // Use errorLogger instead of console.error
      const logger = errorLogger.createComponentLogger('App');
      logger.error('Failed to initialize API', err);
    });
    
    // Clean up old localStorage loginAsCustomer data (migrated to sessionStorage)
    if (localStorage.getItem('loginAsCustomer')) {
      // Silently clean up old data
      localStorage.removeItem('loginAsCustomer');
    }
    
    // Clean up any old state tokens (older than 1 minute)
    const now = Date.now();
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('loginAsCustomer_')) {
        try {
          const data = JSON.parse(localStorage.getItem(key) || '{}');
          if (!data.timestamp || (now - data.timestamp) > 60000) { // Older than 1 minute
            keysToRemove.push(key);
          }
        } catch {
          keysToRemove.push(key); // Remove invalid entries
        }
      }
    }
    keysToRemove.forEach(key => {
      console.log('[App] Cleaning up old state token:', key);
      localStorage.removeItem(key);
    });
    
    // Check if we're on an old /portal/* URL and redirect
    const currentPath = window.location.pathname;
    if (currentPath.startsWith('/portal/')) {
      const newPath = currentPath.replace('/portal/', '/');
      window.location.href = newPath + window.location.search + window.location.hash;
      return;
    }

    // Check for invalid auth state on app load
    const checkAuthState = () => {
      const accessToken = localStorage.getItem('access_token');
      const refreshToken = localStorage.getItem('refresh_token');

      // REMOVED THE BUG: This was clearing localStorage during phone searches!
      // Having an access token without refresh token is OK - the refresh might be in sessionStorage
      // or we might be in the middle of a token refresh operation
      // Only clear if we have neither token
      if (!accessToken && !refreshToken) {
        // No tokens at all - ensure member data is cleared
        localStorage.removeItem('member');
        localStorage.removeItem('member_data');
        sessionStorage.removeItem('member');
      }
    };

    checkAuthState();

    // Load configuration from backend in the background
    // Don't block app initialization
    config.loadConfig()
      .catch((error) => {
        const logger = errorLogger.createComponentLogger('App');
        logger.error('Config load error', error, false);
      });
    
    // Load branding from database including favicon
    brandingService.applyBranding()
      .catch((error) => {
        const logger = errorLogger.createComponentLogger('App');
        logger.error('Branding load error', error, false);
      });
  }, []);
  
  return (
    <AppErrorBoundary>
      <AuthErrorBoundary>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <ErrorNotification />
          <Router>
            <AuthProvider>
              <CustomerFilterProvider>
                <WebPhoneProvider>
            <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/create-password" element={<CreatePassword />} />
            <Route path="/verify-signup-phone" element={<VerifySignupPhone />} />
            <Route path="/webphone-popup" element={<WebPhonePopup />} />

            {/* Protected routes */}
            <Route element={<ProtectedRouteStable />}>
              <Route element={<Layout />}>
                {/* Regular user routes */}
                <Route path="/voicemail" element={<Voicemail />} />
                <Route path="/team" element={<TeamPresence />} />
                <Route path="/my-calls" element={<UserCallHistory />} />
                <Route path="/calendar" element={<Calendar />} />
                <Route path="/settings" element={<Extensions userOnly={true} />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/billing" element={<Billing />} />
                
                {/* Admin routes - customer admin, partner admin and master admin */}
                <Route element={<ProtectedRoute allowedRoles={['admin', 'reseller_admin', 'master_admin']} />}>
                  <Route path="/call-log" element={<CallHistory />} />
                  <Route path="/recording-rules" element={<RecordingRules />} />
                  <Route path="/recording-settings" element={<RecordingSettings />} />
                  <Route path="/extensions" element={<Extensions />} />
                  <Route path="/users" element={<UsersAsExtensions />} />
                  <Route path="/ring-groups" element={<RingGroupsV2 />} />
                  <Route path="/phone-numbers" element={<PhoneNumbers />} />
                  <Route path="/ivr-menus" element={<IVR />} />
                  <Route path="/time-conditions" element={<TimeConditions />} />
                  <Route path="/announcements" element={<div style={{ padding: '20px' }}><h2>Announcements</h2><p>Announcements page coming soon...</p></div>} />
                  <Route path="/call-queue" element={<div style={{ padding: '20px' }}><h2>Call Queue</h2><p>Call queue page coming soon...</p></div>} />
                  <Route path="/queue-announcements" element={<div style={{ padding: '20px' }}><h2>Queue Announcements</h2><p>Queue announcements page coming soon...</p></div>} />
                  <Route path="/consecutive-calls" element={<div style={{ padding: '20px' }}><h2>Consecutive Calls</h2><p>Consecutive calls page coming soon...</p></div>} />
                  <Route path="/hardware" element={<div style={{ padding: '20px' }}><h2>Hardware</h2><p>Hardware page coming soon...</p></div>} />
                  <Route path="/billing" element={<Billing />} />
                  <Route path="/billing/post-payment" element={<PaymentPosting />} />
                  <Route path="/softphones" element={<Softphones />} />
                  <Route path="/prompts" element={<Prompts />} />
                </Route>
                
                {/* Master Admin only routes */}
                <Route element={<ProtectedRoute allowedRoles={['master_admin']} />}>
                  <Route path="/customers-old" element={<Customers />} />
                  <Route path="/customers" element={<CustomersAsExtensions />} />
                  <Route path="/extensions-quota" element={<ExtensionsQuota />} />
                  <Route path="/signup-manager" element={<SignupManager />} />
                  <Route path="/porting" element={<Porting />} />
                </Route>
                
                {/* Admin routes (both master and reseller) */}
                <Route element={<ProtectedRoute allowedRoles={['master_admin', 'reseller_admin']} />}>
                  <Route path="/customer-view" element={<CustomerView />} />
                  <Route path="/system-settings" element={<div style={{ padding: '20px' }}><h2>System Settings</h2><p>System settings page coming soon...</p></div>} />
                  <Route path="/service-packages" element={<ServicePackages />} />
                  <Route path="/billing/pad-files" element={<PADFileManagement />} />
                  <Route path="/addon-services" element={<AddOnServices />} />
                  <Route path="/internet-services" element={<InternetServices />} />
                  <Route path="/internet-vendors" element={<InternetVendorManagementV2 />} />
                  <Route path="/bundle-management" element={<BundleManagement />} />
                  <Route path="/internet-debug" element={<InternetServicesDebug />} />
                  <Route path="/did-deletion" element={<DIDDeletion />} />
                  <Route path="/reseller-management" element={<ResellerManagement />} />
                  <Route path="/api-keys" element={<div style={{ padding: '20px' }}><h2>API Key Management</h2><p>API key management page coming soon...</p></div>} />
                  <Route path="/server-config" element={<div style={{ padding: '20px' }}><h2>Server Config</h2><p>Server configuration page coming soon...</p></div>} />
                  <Route path="/dunning-management" element={<DunningNew />} />
                  <Route path="/brand-management" element={<BrandManagement />} />
                  <Route path="/scheduled-tasks" element={<ScheduledTasks />} />
                </Route>
                
                {/* Master Admin and Partner Admin routes */}
                <Route element={<ProtectedRoute allowedRoles={['master_admin', 'reseller_admin']} />}>
                  <Route path="/activity-log" element={<ActivityLog />} />
                </Route>
                
                {/* Master Admin only routes */}
                <Route element={<ProtectedRoute allowedRoles={['master_admin']} />}>
                  <Route path="/error-logs" element={<ErrorLogs />} />
                  <Route path="/debug-settings" element={<DebugSettings />} />
                  <Route path="/issues-tracking" element={<IssuesTracking />} />
                  <Route path="/pci-compliance" element={<PCICompliance />} />
                </Route>
                
                {/* Reseller Admin routes */}
                <Route element={<ProtectedRoute allowedRoles={['reseller_admin', 'master_admin']} />}>
                  <Route path="/reseller/customers" element={<ComingSoon title="My Customers" message="Customer management for resellers is coming soon." />} />
                  <Route path="/reseller/commissions" element={<ComingSoon title="Commission Reports" message="Commission tracking and reporting is under development." />} />
                  <Route path="/reseller/branding" element={<ComingSoon title="Branding Settings" message="Custom branding options will be available soon." />} />
                </Route>
              </Route>
            </Route>
            
            {/* Default redirect */}
            <Route path="/" element={<Navigate to="/voicemail" replace />} />
          </Routes>
                </WebPhoneProvider>
              </CustomerFilterProvider>
        </AuthProvider>
      </Router>
    </ThemeProvider>
    </AuthErrorBoundary>
    </AppErrorBoundary>
  );
}

export default App;
